import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { updateOldPublishedEventInPlace } from '../helpers/helpers';
import { getLinkedVenue, makeDefaultPublishInfoExcludedFields, PublishInfo, PublishStatus, ShareableItem } from 'globals/store/share.data';
import { FiltersService } from './filters.service';
import { LogService } from './log.service';
import { traceNames } from './performance.tracking.data';
import { UserService } from './user.service';
import { ShareService } from 'src/app/services/share.service';
import { apiLink } from '../helpers/app-constants';
import { transformObservableToSinglePromise } from '../helpers/angular-helpers';
import { PublicEventData } from 'globals/store/public.model';
// import { firestore } from 'firebase';
import * as firebase from 'firebase/app';
// import 'firebase/firestore';
import { environment } from '../../environments/environment';
import { take } from 'rxjs/operators';
import { filterEventDataByFields, publicEventDataReselector, PublishReselectorData } from '../store/reselectors/events.reselector';
import { FilterGroup, filtersFeature } from '../store/filters.era';
import { JoiFile } from 'globals/store/files.model';
import { ProgramService } from './program.service';
import { JoiEvent } from 'src/app/store/events.era';
import { ShareEventListService } from './share.event-list.service';
import { createHomePageData } from 'globals/store/publish.data';
import { jsonCopyObject } from 'globals/helpers/helpers';
import { shareActions, shareFeature } from '../store/share.era';
import { deleteDoc, doc, Firestore, serverTimestamp, setDoc, updateDoc } from '@angular/fire/firestore';
import { lastValueFrom } from 'rxjs';

const PUBLISH_VERSION = 2.0

@Injectable({ providedIn: 'root' })
export class SharePublishService {
  readonly _file = 'share.publish.service.ts'
  constructor(
    private http:HttpClient,
    private log:LogService,
    private userService: UserService,
    private filtersService: FiltersService,
    public store: Store,
    private shareService: ShareService,
    private programService: ProgramService,
    private shareEventListService: ShareEventListService,
    private db: Firestore,
  ) {}

  async getCurrentPublishInfo(publishCode: string){
    try {
      const publishInfo = await lastValueFrom(this.store.pipe(select(shareFeature.selectPublish), take(1)))
      return publishInfo[publishCode]
    } catch (error) {
      console.log(error)
      return null
    }
  }

  async upsertPublishInfo(info: PublishInfo) {
    Object.keys(info)
      .filter(k => !info[k])
      .forEach(key=> delete info[key])
    return await this.shareService.upsertShareInfo({ info, accountId: this.shareService.accountId }, info.publishCode)
  }
  
  async addNewPublishView(title: string, description: string = null, existingPublishedCount: Number = 0) {
    //this.perf.start(traceNames.createProgramPublish);
    
    const { firstName, lastName, uid } = this.userService
    const date = new Date()
    const userName = `${firstName} ${lastName}`
    const excludedFieldIds = makeDefaultPublishInfoExcludedFields()
    const publishCode = await this.shareService.createSlugCode()
    const info: PublishInfo = {
      creatorUid: uid, creator: userName, createdAt: date, excludedFieldIds, publishCode, title: title || null, description: description || null, status: 'never published',
      homePageData: createHomePageData()
    } as PublishInfo

    const insertObject:ShareableItem = {
      type:'publish',
      eventId: this.shareService.eventId,
      code: info.publishCode,
      info,
      accountId: this.shareService.accountId,
      timestamp: serverTimestamp()
    }
    try {
      await setDoc(doc(this.db, '/shareables/' + publishCode), insertObject)

      // this.perf.stop(traceNames.createProgramPublish, {attributes:[
      //   {name:"ExistingPublishes", value:existingPublishedCount},
      // ]})
    } catch (error) {
      this.log.error({
        file:this._file,
        data: {insertObject: insertObject},
        function:'addNewPublishView -> insertPublishInfo',
        message:'There was an error creating the publish view',
        error
      })
      return;
      // this.perf.stop(traceNames.createProgramPublish, {attributes:[
      //   {name:"ExistingPublishes", value:existingPublishedCount},
      //   {name:"Error", value:"ErrorAddingPublish"},
      // ]})

    }
    return publishCode
  }

  async changePublishViewNameDescription(publishInfo: PublishInfo, title: string, description: string) {
    try {
      publishInfo = jsonCopyObject(publishInfo)
      publishInfo.title = title
      publishInfo.description = description
      await this.upsertPublishInfo(publishInfo)
    } catch (error) {
      this.log.error({
        file:this._file,
        function:'changePublishViewName',
        message:'There was an error updating the event',
        error
      })
    }
  }

  async changePublishViewDescription(publishInfo: PublishInfo, desc: string) {
    try {
      publishInfo = jsonCopyObject(publishInfo)
      publishInfo.description = desc
      await this.upsertPublishInfo(publishInfo)
    } catch (error) {
      this.log.error({
        file:this._file,
        function:'changePublishViewDescription',
        message:'There was an error updating the event',
        error
      })
    }
  }

  async changePublishViewCTALinks(publishInfo: PublishInfo, CTALinks: JoiFile[]) {
    try {
      publishInfo = jsonCopyObject(publishInfo)
      publishInfo.CTALinks = CTALinks
      await this.upsertPublishInfo(publishInfo)
    } catch (error) {
      this.log.error({
        file:this._file,
        function:'changePublishViewName',
        message:'There was an error updating the event',
        error
      })
    }
  }

  async addPublishFilters(publishInfo: PublishInfo) {
    this.store.select(filtersFeature.selectPublish)
    try {
      const filters = jsonCopyObject(await transformObservableToSinglePromise<FilterGroup>(this.store.pipe(select(filtersFeature.selectPublish))))
      Object.keys(filters).filter(k => !filters[k]).forEach(k => delete filters[k] ) // search was showing up as undefined and firebase was erroring because it can't handle undefined
      const newInfo = { ...publishInfo, filters }
      this.store.dispatch(shareActions.loadShareablePublish(newInfo.publishCode, newInfo))
      // await this.upsertPublishInfo(newInfo) // don't save just update the slice
    } catch (error) {
      this.log.error({
        file:this._file,
        function:'addExcludedFields',
        message:'There was an error updating the event',
        error
      })
    }
  }

  
  async addExcludedFields(publishInfo: PublishInfo, excludedFieldIds: string[], tabNames: { [tId: string]: string } = null) {
    try {
      const newInfo = { ...publishInfo, excludedFieldIds }
      if (tabNames) newInfo.tabNames = tabNames
      // if (venue) newInfo.venue = 
      this.store.dispatch(shareActions.loadShareablePublish(newInfo.publishCode, newInfo))
      // await this.upsertPublishInfo(newInfo)
    } catch (error) {
      this.log.error({
        file:this._file,
        function:'addExcludedFields',
        message:'There was an error updating the event',
        error
      })
    }
  }

  async saveVenueIdToPublishInfo(publishCode: string, venueId: string) {
    const publishInfo = jsonCopyObject<PublishInfo>(await this.getCurrentPublishInfo(publishCode))
    console.log(`Saving venue id to publishInfo.  Old: [${publishInfo.venueId}], New: [${venueId}]`)
    publishInfo.venueId = venueId
    await this.upsertPublishInfo(publishInfo)
  }

  async postPublishedEventData(publishData: PublicEventData, publishInfo: PublishInfo) {

    publishData.publishVersion = PUBLISH_VERSION
    const postData = {
      accountId: this.shareService.accountId,
      eventId: this.shareService.eventId,
      publishCode: publishInfo.publishCode,
      publishedData:JSON.stringify(publishData),
      storageBucket: environment.firebase.storageBucket,
      publishedBefore: publishInfo.status !== 'never published',
    }
    // console.log({ postData, publishData, publishInfo })
    const url = `${apiLink}/save-published-data`
    await transformObservableToSinglePromise(this.http.post(url, postData))
  }

  async duplicatePublishView(eventId:string, publishInfo: PublishInfo, title: string) {
    try {
      const newPublishInfo: PublishInfo = {...jsonCopyObject(publishInfo), title, status: 'never published', publisher: null, publishedAt: null, publisherUid: null, publishCode:'_replace_with_code_'  } //
      await this.shareService.createShareable('publish', eventId, newPublishInfo)
    } catch (error) {
        this.log.error({
          file: this._file,
          function:'duplicatePublishView -> createShareable',
          message:'There was an error updating the event',
          error
        })
      }
  }


  async downloadAndDispatchPublishedEvent(publishCode, fromEventList = false){
    try {
      const url = `${apiLink}/load-published-data`
      // console.log({ publishCode, fromEventList })
      const publicEventData = await transformObservableToSinglePromise<PublicEventData>(this.http.post(url, { publishCode }))
      // console.log({ publicEventData })
      if (publicEventData.error) throw new Error(`${publicEventData.error}: ${publicEventData['message']}`)
      updateOldPublishedEventInPlace(publicEventData, this.programService.defaultTimeZoneId)
      // console.log("OUT OF UPDATE IN PLACE!", { publishCode, publicEventData })
      this.store.dispatch(shareActions.addPublishedData(publishCode, publicEventData))
    } catch (error) {
      console.log(publishCode, error)
      this.store.dispatch(shareActions.addPublishedData(publishCode, null))
    }
  }
  
  public async updateMultiplePublishViews(publishCodes: string[]): Promise<boolean> {
    try {
      const publishesAndInfos: { eventData: PublicEventData, publishInfo: PublishInfo }[] = []
      for (let i = 0; i < publishCodes.length; i++) { // still need an async forEach
        const publishAndInfo = await this.updatePublishView(publishCodes[i])
        publishesAndInfos.push(publishAndInfo)
      }
      
      console.log({ publishesAndInfos })
      this.shareEventListService.updateEventListsWithPublishViews(publishesAndInfos)
    } catch (error) {
      this.log.error({
        file:this._file,
        function:'updateMultiplePublishView',
        message:'There was an error updating the published timelines',
        error
      })
      return false
    }
    return true
  }

  addPublishDataToPublishInfo(info: PublishInfo, status: PublishStatus) {
    // mutates in place

    const { firstName, lastName, uid } = this.userService
    const date = new Date()
    const userName = `${firstName} ${lastName}`
    info.publisher = userName
    info.publishedAt = date
    info.publisherUid = uid
    info.status = status
  }

  async publishEvent(info: PublishInfo, title: string = '', currentEvent: JoiEvent) {
    try {
      const { eventData }: PublishReselectorData = await transformObservableToSinglePromise<PublishReselectorData>(this.store.pipe(select(publicEventDataReselector, { publishCode: info.publishCode, filter: true })));

      if(!eventData) throw new Error('No data exists to publish')
      this.addPublishDataToPublishInfo(info, 'published')
      info.title = title

      eventData.hideMadeWithJoi = info.excludedFieldIds && info.excludedFieldIds.includes("madeWithJoi")
      eventData.excludedFieldIds = info.excludedFieldIds || []

      // Overwrite any existing tabnames
      if (info.tabNames) {
        eventData.tabNames = info.tabNames
      }
      if (info.CTALinks) eventData.CTALinks = info.CTALinks
      
      eventData.venue = getLinkedVenue(currentEvent, info?.excludedFieldIds, info.venueId, eventData.timeline)      
      // currentEvent.venues[info.venueId] || null

      await this.postPublishedEventData(eventData, info)
      this.store.dispatch(shareActions.addPublishedData(info.publishCode, eventData))
      await this.upsertPublishInfo(info)
      const publishesAndInfos: { eventData: PublicEventData, publishInfo: PublishInfo }[] = [{ eventData, publishInfo: info }]
      this.shareEventListService.updateEventListsWithPublishViews(publishesAndInfos)

      return false
    } catch (error) {
      this.log.error({
        file:this._file,
        function:'publishEvent',
        message:'There was an error publishing the event',
        error
      })
      throw(error)
    }

  }


  private async updatePublishView(publishCode: string): Promise<{ eventData: PublicEventData, publishInfo: PublishInfo }> {
    // no error handling as it will be handled in the multi or single public methods
    let { eventData } = await transformObservableToSinglePromise<PublishReselectorData>(this.store.pipe(select(publicEventDataReselector, { publishCode, filter: false })));
    if (!eventData) throw new Error("Update publish view failed!  Trying to update publish data with empty event data")

    const publishInfo = await this.getCurrentPublishInfo(publishCode)
    this.addPublishDataToPublishInfo(publishInfo, 'published')

    const filters = !publishInfo.filters ? null : await this.filtersService.filterPublishViewFilters(publishInfo.filters)
    eventData = filterEventDataByFields(publishInfo.excludedFieldIds, eventData, filters)
    eventData.hideMadeWithJoi = publishInfo.excludedFieldIds && publishInfo.excludedFieldIds.includes("madeWithJoi")
    eventData.excludedFieldIds = publishInfo.excludedFieldIds 
    eventData.tabNames = publishInfo.tabNames
    if (publishInfo.CTALinks) eventData.CTALinks = publishInfo.CTALinks
    await this.postPublishedEventData(eventData, publishInfo)
    this.store.dispatch(shareActions.addPublishedData(publishInfo.publishCode, eventData))
    await this.upsertPublishInfo(publishInfo)
    return { eventData, publishInfo }
  }

  async deletePublishData(publishCode: string) {
    // only works for publishes that are currently published, if you see an error going forward
    await transformObservableToSinglePromise(this.http.post(`${apiLink}/delete-published-data`, {publishCode}))
  }

  async deletePublishViews(publishInfos: PublishInfo[]) {

    const promise = Promise.all(publishInfos.filter(p => p.status === 'published').map(p => this.deletePublishData(p.publishCode)))
    await this.log.executePromiseLogError(this, promise, 'deletePublishViews', `There was an error deleting published data`)
    // needs to be done in order
    const promise2 = Promise.all(publishInfos.map(({ publishCode }) => deleteDoc(doc(this.db, `/shareables/${publishCode}`))))
    await this.log.executePromiseLogError(this, promise2, 'deletePublishViews', `There was an error deleting publish infos`)
    
    this.shareEventListService.deletePublishViewsFromEventLists(publishInfos)
  }

  async unpublishPublishView(publishCode:string) { // returns a boolean for whehter there is an error
    await this.log.executePromiseLogError(this, this.deletePublishData(publishCode), 'unpublishPublishView', `There was an error unpublishing the event`)

    const info = await this.getCurrentPublishInfo(publishCode)
    this.addPublishDataToPublishInfo(info, 'unpublished')

    const updateObject = { info }
    const promise = updateDoc(doc(this.db, '/shareables/' + publishCode), updateObject)
    const noError = await this.log.executePromiseLogError(this, promise, 'unpublishPublishView', `There was an error updating the shareabel when unpublishing the event`)
    console.log({ updateObject, noError })
    if (noError) {
      const noErrorWithEventLists = await this.shareEventListService.unpublishPublishViewFromEventLists(publishCode)
      return !noErrorWithEventLists
    }
    return !noError
  }

}